// File: packages/i18n/src/LocaleProvider.tsx
// Version: 2026.4.1 | ForgeTrack locale context
// CRITICAL: import from ./types, NEVER from ./index

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { type Locale, type TextDirection, DEFAULT_LOCALE, SUPPORTED_LOCALES, getDirection } from './types';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  direction: TextDirection;
}

const LocaleContext = createContext<LocaleContextType>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  direction: 'ltr',
});

const COOKIE_NAME = 'ft-locale';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

function readCookieLocale(): Locale | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`${COOKIE_NAME}=([^;]+)`));
  if (!match) return null;
  const value = match[1] as Locale;
  return SUPPORTED_LOCALES.includes(value) ? value : null;
}

function writeCookieLocale(locale: Locale): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=${locale};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`;
}

function applyLocaleToDocument(locale: Locale): void {
  if (typeof document === 'undefined') return;
  const dir = getDirection(locale);
  document.documentElement.setAttribute('data-locale', locale);
  document.documentElement.setAttribute('lang', locale);
  document.documentElement.setAttribute('dir', dir);
}

export function LocaleProvider({
  children,
  defaultLocale,
}: {
  children: React.ReactNode;
  defaultLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    return readCookieLocale() ?? defaultLocale ?? DEFAULT_LOCALE;
  });

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    writeCookieLocale(newLocale);
    applyLocaleToDocument(newLocale);
  }, []);

  // Apply on mount
  useEffect(() => {
    applyLocaleToDocument(locale);
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    setLocale,
    direction: getDirection(locale),
  }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
